import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
  initializeFirestore, 
  persistentLocalCache, 
  persistentSingleTabManager, 
  memoryLocalCache, 
  setLogLevel 
} from 'firebase/firestore';
import { getAuth, setPersistence, browserLocalPersistence, GoogleAuthProvider } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getDatabase } from 'firebase/database';

function readEnv(name: string): string {
  const value = (import.meta as any).env?.[name];
  if (!value || typeof value !== 'string') {
    throw new Error(`Missing environment variable ${name}. Create a .env.local with VITE_FIREBASE_* values and restart the dev server.`);
  }
  return value;
}

function readEnvOptional(name: string): string | undefined {
  const value = (import.meta as any).env?.[name];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

const firebaseConfig = {
  apiKey: readEnv('VITE_FIREBASE_API_KEY'),
  authDomain: readEnv('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: readEnv('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: readEnv('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: readEnv('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: readEnv('VITE_FIREBASE_APP_ID'),
  // Optional analytics
  measurementId: readEnvOptional('VITE_FIREBASE_MEASUREMENT_ID'),
  databaseURL: readEnvOptional('VITE_FIREBASE_DATABASE_URL'),
};

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

const DEV = !!((import.meta as any).env?.DEV);
const AUTO_DETECT_LONG_POLLING = true;
const USE_FETCH_STREAMS = false;

export const db = initializeFirestore(app, {
  experimentalAutoDetectLongPolling: AUTO_DETECT_LONG_POLLING,
  useFetchStreams: USE_FETCH_STREAMS,
  localCache: persistentLocalCache({ tabManager: persistentSingleTabManager({}) }),
} as any);

// Reduce Firebase console noise in development
// Options: 'debug' | 'error' | 'silent'
if (DEV) {
  setLogLevel('silent'); // Suppress all logs including permission errors
}

// eslint-disable-next-line no-console
console.info(`[Firebase] Firestore transport: autoDetectLongPolling=${AUTO_DETECT_LONG_POLLING} fetchStreams=${USE_FETCH_STREAMS} (DEV=${DEV})`);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();
export const storage = getStorage(app);
export const rtdb = getDatabase(app);

// Set a safe default auth persistence so sessions don't vanish unexpectedly.
// Login page can still override to session persistence when "Remember me" is off.
try {
  // Do not await; fire-and-forget is fine at bootstrap
  setPersistence(auth, browserLocalPersistence).catch(() => {/* ignore */});
} catch {
  // ignore
}


export default app;
